/* ==========================================================================
   Hidden Garden — location page system (.hgl)
   Powers two widgets shared by the hgl-* sections:
     1. same-day cutoff countdown  [data-hgl-clock]
     2. coverage ledger tabs       [data-hgl-tabs]

   The delivery-area check is deliberately NOT here: the store runs a zip-code
   app that owns the real delivery list, and the sections expose an @app block
   slot for it rather than duplicating that logic.

   Loaded by more than one section, so everything is guarded and delegated —
   including it twice must not double-bind.
   ========================================================================== */
(function () {
  "use strict";

  if (window.__hglLocationInit) return;
  window.__hglLocationInit = true;

  /* ---------- 1. cutoff countdown --------------------------------------- */

  function pacificNow() {
    var parts = {};
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    })
      .formatToParts(new Date())
      .forEach(function (p) { parts[p.type] = p.value; });
    return { h: (+parts.hour) % 24, m: +parts.minute, s: +parts.second };
  }

  function tickClocks() {
    var nodes = document.querySelectorAll("[data-hgl-clock]");
    if (!nodes.length) return;
    var now = pacificNow();

    nodes.forEach(function (el) {
      /* Prefer the absolute instant the section renders in the SHOP's offset.
         Hard-coding America/Los_Angeles here while Liquid used the shop's
         timezone made the two disagree whenever those differed — the server
         rendered "order within ..." and this immediately flipped it to
         "cutoff has passed". Counting to a fixed instant removes the question:
         both sides mean the same moment, whatever the viewer's timezone. */
      var left;
      var at = el.getAttribute("data-cutoff-at");
      if (at) {
        var t = Date.parse(at);
        if (!isNaN(t)) left = Math.floor((t - Date.now()) / 1000);
      }
      if (typeof left !== "number") {
        /* Fallback for markup rendered before data-cutoff-at existed. */
        var cutoff = parseInt(el.getAttribute("data-cutoff") || "13", 10);
        left = ((cutoff - now.h) * 3600) - (now.m * 60) - now.s;
      }

      /* Both states are rendered server-side and one is hidden, so this only
         toggles them. Past the cutoff the WHOLE phrase changes, not just the
         number — swapping the value alone left "Order within tomorrow for
         delivery today" on screen: incoherent, and a same-day promise the
         shop can no longer keep. */
      var openEl = el.querySelector("[data-hgl-clock-open]");
      var closedEl = el.querySelector("[data-hgl-clock-closed]");

      /* On the board layout the clock sits in a labelled row, and the label has
         to move with it: leaving it fixed rendered "Cut off: Tomorrow" once the
         cutoff had gone, which is a false statement rather than a stale one. */
      function boardKey(closed) {
        var row = el.closest ? el.closest(".hgl-board__row") : null;
        var k = row && row.querySelector("[data-hgl-board-key]");
        if (!k) return;
        var want = closed
          ? (k.getAttribute("data-key-closed") || "Next run")
          : (k.getAttribute("data-key-open") || "Cut off");
        if (k.textContent.trim() !== want) k.textContent = want;
      }

      if (left <= 0) {
        if (openEl) openEl.hidden = true;
        if (closedEl) closedEl.hidden = false;
        el.classList.add("is-closed");
        boardKey(true);
        return;
      }

      if (openEl) openEl.hidden = false;
      if (closedEl) closedEl.hidden = true;
      el.classList.remove("is-closed");
      boardKey(false);

      /* Swap the static "Order by 1pm" sentence for the live countdown. The
         static one ships visible so a blocked or slow script never leaves an
         em dash standing in for the number. */
      var staticEl = el.querySelector("[data-hgl-clock-static]");
      var liveEl = el.querySelector("[data-hgl-clock-live]");
      if (staticEl) staticEl.hidden = true;
      if (liveEl) liveEl.hidden = false;

      var hh = Math.floor(left / 3600);
      var mm = Math.floor((left % 3600) / 60);
      var ss = left % 60;
      var out = hh + "h " + String(mm).padStart(2, "0") + "m " + String(ss).padStart(2, "0") + "s";
      var target = el.querySelector("[data-hgl-clock-value]") || el;
      if (target.textContent !== out) target.textContent = out;
    });
  }

  tickClocks();
  setInterval(tickClocks, 1000);

  /* ---------- 2. ledger tabs -------------------------------------------- */

  /* Mark each tablist as enhanced. Until this runs the stylesheet shows every
     panel and hides the tab strip, so with JavaScript blocked or still loading
     the reader gets all of the area content stacked rather than one panel and
     five dead tabs. Re-run on section load so the theme editor behaves too. */
  function enhanceTabs() {
    document.querySelectorAll("[data-hgl-tabs]").forEach(function (g) {
      g.classList.add("is-enhanced");
    });
  }
  enhanceTabs();
  document.addEventListener("shopify:section:load", enhanceTabs);

  document.addEventListener("click", function (e) {
    var tab = e.target.closest ? e.target.closest("[data-hgl-tab]") : null;
    if (!tab) return;
    var group = tab.closest("[data-hgl-tabs]");
    if (!group) return;
    e.preventDefault();

    var id = tab.getAttribute("data-hgl-tab");
    group.querySelectorAll("[data-hgl-tab]").forEach(function (t) {
      var on = t === tab;
      t.setAttribute("aria-selected", on ? "true" : "false");
      /* Keep the roving tabindex in sync. The sections render it correctly on
         load, but without this the tab order would be frozen on whichever tab
         happened to be first — so after any selection, Tab would jump back to
         a tab the user is no longer on. */
      t.setAttribute("tabindex", on ? "0" : "-1");
    });
    group.querySelectorAll("[data-hgl-panel]").forEach(function (p) {
      p.classList.toggle("is-on", p.getAttribute("data-hgl-panel") === id);
    });
  });

  /* Keyboard support for the tablist: left/right move between tabs. */
  document.addEventListener("keydown", function (e) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    var tab = e.target.closest ? e.target.closest("[data-hgl-tab]") : null;
    if (!tab) return;
    var group = tab.closest("[data-hgl-tabs]");
    if (!group) return;
    var tabs = Array.prototype.slice.call(group.querySelectorAll("[data-hgl-tab]"));
    var i = tabs.indexOf(tab);
    var next = tabs[e.key === "ArrowRight" ? i + 1 : i - 1];
    if (!next) return;
    e.preventDefault();
    next.focus();
    next.click();
  });

  /* ---------- order desk cutoff (local-page-5) ---------------------------- */
  /* Two discrete blocks, not one block with swapped strings. Swapping produced
     "order now for tomorrow" sitting above "order by 1pm today", which
     contradicted itself; and re-pointing the arc at tomorrow's cutoff drew a
     chart of a deadline nobody is racing. After the cutoff the arc is removed. */
  /* Wall clock in the shop's timezone, recomputed every tick. Nothing here
     depends on when the HTML was rendered, which is what makes it survive a
     tab left open overnight and a page served from cache the next morning. */
  function shopClock(tz) {
    var p = {};
    try {
      new Intl.DateTimeFormat("en-US", {
        timeZone: tz, hour12: false,
        hour: "2-digit", minute: "2-digit", second: "2-digit"
      }).formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
      return { h: (+p.hour) % 24, m: +p.minute, s: +p.second };
    } catch (e) {
      /* an unknown timezone string must not take the whole hero down */
      var d = new Date();
      return { h: d.getHours(), m: d.getMinutes(), s: d.getSeconds() };
    }
  }

  function tickDesk() {
    var open = document.querySelector("[data-hgl-desk-open]");
    var shut = document.querySelector("[data-hgl-desk-shut]");
    if (!open || !shut) return;

    var cutoff = parseInt(open.getAttribute("data-cutoff") || "13", 10);
    var tz = open.getAttribute("data-tz") || "America/Los_Angeles";
    var now = shopClock(tz);

    /* seconds until today's cutoff, in the shop's own day. Negative once it
       has passed, and back to a full day's worth the moment the clock rolls
       past midnight — which is the whole point of computing it here. */
    var left = ((cutoff - now.h) * 3600) - (now.m * 60) - now.s;
    var closed = left <= 0;

    open.hidden = closed;
    shut.hidden = !closed;
    if (closed) return;

    var h = Math.floor(left / 3600);
    var m = Math.floor((left % 3600) / 60);
    var sec = left % 60;

    var hrs = document.querySelector("[data-hgl-desk-hrs]");
    if (hrs) hrs.textContent = Math.max(1, h) + "h";

    var lead = document.querySelector("[data-hgl-desk-lead]");
    if (lead) lead.textContent = "Order within " + h + "h " + String(m).padStart(2, "0") + "m " + String(sec).padStart(2, "0") + "s";

    var dial = document.querySelector("[data-hgl-desk-dial]");
    /* fills across the ordering day toward the cutoff */
    if (dial) dial.style.setProperty("--pct", Math.max(4, Math.min(100, (1 - (left / 3600) / cutoff) * 100)) + "%");
  }
  tickDesk();
  setInterval(tickDesk, 1000);

  /* ---------- hero CTA scrolls to the main product grid ------------------- */
  document.addEventListener("click", function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute("href").slice(1);
    if (!id) return;
    var t = document.getElementById(id);
    if (!t) return;
    e.preventDefault();
    var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    t.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  });

})();